import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { Car, Users, UserCheck, Clock, TrendingUp, CheckCircle } from 'lucide-react';
import { getAdminStats } from '../../services/api';

const COLORS = ['#5b2d8e', '#7c4ff2', '#f59e0b', '#10b981', '#ef4444', '#6366f1'];

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getAdminStats().then(r => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-700" /></div>;

  const { stats, monthlyData, statusBreakdown } = data || {};

  const STAT_CARDS = [
    { label: 'Total Bookings', value: stats?.totalBookings || 0, icon: Car, color: 'bg-purple-100 text-purple-700', change: 'All time' },
    { label: 'Today\'s Bookings', value: stats?.todayBookings || 0, icon: Clock, color: 'bg-blue-100 text-blue-700', change: 'Today' },
    { label: 'Pending Review', value: stats?.pendingBookings || 0, icon: TrendingUp, color: 'bg-yellow-100 text-yellow-700', change: 'Action needed' },
    { label: 'Completed Rides', value: stats?.completedBookings || 0, icon: CheckCircle, color: 'bg-green-100 text-green-700', change: 'All time' },
    { label: 'Total Users', value: stats?.totalUsers || 0, icon: Users, color: 'bg-indigo-100 text-indigo-700', change: 'Registered' },
    { label: 'Active Drivers', value: stats?.activeDrivers || 0, icon: UserCheck, color: 'bg-teal-100 text-teal-700', change: 'On roster' },
  ];

  return (
    <div className="space-y-6 animate-fade-up">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 font-heading">Operations Dashboard</h1>
        <p className="text-sm text-gray-500">MDS Transportation — Live overview</p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {STAT_CARDS.map(({ label, value, icon: Icon, color, change }) => (
          <div key={label} className="card border border-gray-100 flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${color}`}><Icon size={22} /></div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{value.toLocaleString()}</p>
              <p className="text-xs font-semibold text-gray-700">{label}</p>
              <p className="text-xs text-gray-400">{change}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Revenue highlight */}
      {stats?.totalRevenue > 0 && (
        <div className="card border border-green-100 bg-green-50 flex items-center gap-4">
          <div className="text-3xl font-bold text-green-700">${parseFloat(stats.totalRevenue).toFixed(2)}</div>
          <div>
            <p className="font-semibold text-green-800">Total Revenue Collected</p>
            <p className="text-sm text-green-600">From paid bookings</p>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Monthly bookings chart */}
        {monthlyData?.length > 0 && (
          <div className="card border border-gray-100">
            <h2 className="font-bold text-gray-900 mb-4 font-heading">Monthly Bookings (Last 6 Months)</h2>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={monthlyData}>
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#5b2d8e" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Status breakdown pie */}
        {statusBreakdown?.length > 0 && (
          <div className="card border border-gray-100">
            <h2 className="font-bold text-gray-900 mb-4 font-heading">Booking Status Breakdown</h2>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={statusBreakdown} dataKey="count" nameKey="status" cx="50%" cy="50%" outerRadius={80} label={({ status, percent }) => `${status} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                  {statusBreakdown.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Quick links */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Manage Bookings', path: '/admin/bookings', emoji: '📋' },
          { label: 'Manage Users', path: '/admin/users', emoji: '👥' },
          { label: 'Manage Drivers', path: '/admin/drivers', emoji: '🚗' },
          { label: 'View Contacts', path: '/admin/contacts', emoji: '📬' },
        ].map(({ label, path, emoji }) => (
          <Link key={path} to={path} className="card border border-gray-100 text-center hover:shadow-md hover:border-purple-200 transition-all cursor-pointer">
            <div className="text-3xl mb-2">{emoji}</div>
            <p className="text-sm font-semibold text-gray-700">{label}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}
